import { IdCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { workspaces, unionWorkspaceEntries, type NavigationEntry } from '@/config/navigation';
import { VIEWS } from '@/config/views';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCanSee } from '@/shared/hooks/usePermission';
import { cn } from '@/shared/lib/cn';

import { BrandMark } from './BrandMark';

function NavItem({ entry }: { entry: NavigationEntry }) {
  const { t } = useTranslation();
  const view = VIEWS[entry.view];
  const canSee = useCanSee(entry.view);

  if (!canSee || !view.labelKey) return null;

  return (
    <NavLink
      to={view.path}
      end={view.path === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-(--radius-control) px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-brand-900 font-medium text-white'
            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        )
      }
    >
      {entry.icon}
      {t(view.labelKey)}
    </NavLink>
  );
}

export function Sidebar() {
  const { user } = useAuth();
  const isGlobal = user?.scope.is_global ?? false;
  const roles = isGlobal ? Object.keys(workspaces) : (user?.role_codes ?? []);
  const entries = unionWorkspaceEntries(workspaces, roles);
  const showGenericHome = !isGlobal && !roles.includes('EXECUTIVE');

  return (
    <aside className="bg-surface border-border hidden w-60 shrink-0 border-r p-3 md:block">
      <BrandMark className="mb-6" />
      <nav className="flex flex-col gap-0.5">
        {showGenericHome && (
          <NavItem entry={{ view: 'home', icon: <IdCard className="size-4" aria-hidden /> }} />
        )}
        {entries.map((entry) => (
          <NavItem key={entry.view} entry={entry} />
        ))}
      </nav>
    </aside>
  );
}
